// src/lib/motion.tsx
import React from 'react';
import { motion as framerMotion, MotionProps, AnimatePresenceProps } from 'framer-motion';
import { AnimatePresence as FramerAnimatePresence } from 'framer-motion';

// This overrides the default fade+slide animation with just fade
// These are the default motion variants used throughout the app
const defaultMotionVariants = {
  initial: { opacity: 1 }, // Changed from 0 to 1 to prevent fade-in
  animate: { opacity: 1 },
  exit: { opacity: 1 }, // Changed from 0 to 1 to prevent fade-out
};

// Override for common variants used throughout the app
export const overrideVariants = {
  // Apply this to container elements - no staggering, just a simple fade
  containerVariants: {
    hidden: { opacity: 1 }, // Changed from 0 to 1 to prevent fade-in
    show: {
      opacity: 1,
      transition: {
        duration: 0, // Changed from 0.2 to 0 for instant transition
        // No staggering for instant transitions
        staggerChildren: 0
      }
    }
  },
  
  // Apply this to item elements - only opacity changes
  itemVariants: {
    hidden: { opacity: 1 }, // Changed from 0 to 1 to prevent fade-in
    show: { opacity: 1 }
  },
  // For elements that should have no animation at all
  noAnimation: {
    hidden: { opacity: 1 },
    show: { opacity: 1 }
  },
  
  // Accordions may need height animation - use carefully
  accordionVariants: {
    collapsed: { 
      opacity: 0,
      height: 0,
      overflow: 'hidden'
    },
    expanded: { 
      opacity: 1,
      height: 'auto',
      overflow: 'hidden'
    }
  }
};

// Specific config to ensure counting animations work properly
export const ensureAnimationPriority = {
  transition: {
    delayChildren: 0,
    staggerChildren: 0,
    when: "beforeChildren"
  }
};

// Create stable motion components that maintain the framer-motion API
// but override the default animations

// motion.div
export const StableMotionDiv = React.forwardRef<HTMLDivElement, MotionProps & React.HTMLAttributes<HTMLDivElement>>((props, ref) => {
  // Merge our simplified animation with any props passed in
  // Props passed directly will override our defaults
  const mergedProps = {
    ...defaultMotionVariants,
    transition: { duration: 0, ...props.transition }, // Changed from 0.2 to 0
    ...props
  };
  
  return <framerMotion.div ref={ref} {...mergedProps} />;
});

// motion.section
export const StableMotionSection = React.forwardRef<HTMLElement, MotionProps & React.HTMLAttributes<HTMLElement>>((props, ref) => {
  const mergedProps = {
    ...defaultMotionVariants,
    transition: { duration: 0, ...props.transition }, // Changed from 0.2 to 0
    ...props
  };
  
  return <framerMotion.section ref={ref} {...mergedProps} />;
});

// motion.article
export const StableMotionArticle = React.forwardRef<HTMLElement, MotionProps & React.HTMLAttributes<HTMLElement>>((props, ref) => {
  const mergedProps = {
    ...defaultMotionVariants,
    transition: { duration: 0, ...props.transition }, // Changed from 0.2 to 0
    ...props
  };
  
  return <framerMotion.article ref={ref} {...mergedProps} />;
});

// motion.span
export const StableMotionSpan = React.forwardRef<HTMLSpanElement, MotionProps & React.HTMLAttributes<HTMLSpanElement>>((props, ref) => {
  const mergedProps = {
    ...defaultMotionVariants,
    transition: { duration: 0, ...props.transition }, // Changed from 0.2 to 0
    ...props
  };
  
  return <framerMotion.span ref={ref} {...mergedProps} />;
});

// AnimatePresence with our defaults
export const AnimatePresence: React.FC<AnimatePresenceProps & { children?: React.ReactNode }> = (props) => {
  return <FramerAnimatePresence {...props} />;
};

// Extract the framer-motion components we want to override
const { 
  div: _div, 
  section: _section, 
  article: _article, 
  span: _span,
  button: _button,
  ul: _ul,
  li: _li,
  ...restOfFramerMotion 
} = framerMotion;

// Main export that defaults to the stable version
export const motion = {
  div: StableMotionDiv,
  section: StableMotionSection,
  article: StableMotionArticle,
  span: StableMotionSpan,
  button: framerMotion.button,
  ul: framerMotion.ul,
  li: framerMotion.li,
  // Add all remaining motion components without overriding our custom ones
  ...restOfFramerMotion
};

// If you need to use the original motion for a specific component,
// you can export the original as well
export const originalMotion = framerMotion;